'use-client';
import * as React from 'react';
import {
  formatClassNames,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import type {
  ICaptchaProps,
  ICaptchaImperativeActions,
} from '../Captcha.types';
import { useRecaptcha } from '../../../providers/useRecaptcha/useRecaptcha';
import { useHasBeenInViewport } from '../../../providers/useHasBeenInViewport/useHasBeenInViewport';
import semanticClassNames from '../Captcha.semanticClassNames';
import { st, classes } from './style/Captcha.st.css';
import logo from './assets/logo.png';

const Captcha: React.ForwardRefRenderFunction<
  ICaptchaImperativeActions,
  ICaptchaProps
> = (props, ref) => {
  const {
    id,
    className,
    customClassNames = [],
    language,
    token,
    isMobileFriendly = true,
    onTokenChange = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    onError = () => {},
    onTimeout = () => {},
    onVerified = () => {},
    useV3 = false,
  } = props;

  const [loaded, setLoaded] = React.useState<boolean>(false);
  const compRef = React.useRef<HTMLDivElement>(null);

  const verifiedCallback = (_token: string) => {
    onTokenChange(_token);
    onVerified({ type: 'onVerified' });
  };

  const expiredCallback = () => {
    onTokenChange(undefined);
    onTimeout({ type: 'onTimeout' });
  };

  const errorCallback = () => {
    onError({ type: 'onError' });
  };

  const loadedCallback = () => {
    setLoaded(true);
  };

  const [captchaRef, { reset: resetCaptcha, execute }] = useRecaptcha({
    language,
    theme: 'light',
    size: 'normal',
    verifiedCallback,
    expiredCallback,
    errorCallback,
    loadedCallback,
    useV3,
  });

  const shouldLoadCaptcha = useHasBeenInViewport(compRef);

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        if (useV3) {
          execute?.();
          return;
        }

        const iframe = document.querySelector(
          `#${id} iframe`,
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe.setAttribute('title', 'Captcha');
          iframe.focus();
        }
      },
      reset: () => {
        onTokenChange(undefined);
        void resetCaptcha();
      },
    };
  }, [id, resetCaptcha, onTokenChange, execute, useV3]);

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(props.a11y)}
      className={st(
        classes.root,
        {
          loaded,
          isMobileFriendly,
        },
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={compRef}
    >
      <div className={classes.captchaLoader}></div>
      {!useV3 && (
        <>
          <div
            className={classes.captcha}
            ref={shouldLoadCaptcha ? captchaRef : null}
          ></div>
          <input
            className={classes.checkbox}
            type="checkbox"
            checked={!!token}
            required
            onChange={() => {}}
            tabIndex={-1}
            aria-hidden="true"
          />
        </>
      )}
      {useV3 && (
        <div
          ref={shouldLoadCaptcha ? captchaRef : null}
          className={classes.captchaV3}
        >
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img src={logo} />
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(Captcha);
